import { useEditorState } from '@tiptap/react';
import { cx } from 'class-variance-authority';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { BlockFenceWrapperProps } from '@/components/block-primitives/block-fence-node-view/block-fence-node-view';
import { type InstructionBlockState, instructionIssues, instructionTarget, visibleValidations } from '../../store/instruction-validation';
import { useOptionalBuilder } from '../../store/provider';
import { useTaskBlockContext } from './task-block-context';
const InstructionValidationBoundary = ({
  children,
  nodeView,
  justInserted
}: BlockFenceWrapperProps) => {
  const {
    editor,
    node,
    getPos
  } = nodeView;
  const builder = useOptionalBuilder();
  const task = useTaskBlockContext();
  const id = useId();
  const root = useRef<HTMLFieldSetElement>(null);
  const [touched, setTouched] = useState(!justInserted);
  const index_0 = useEditorState({
    editor,
    selector: () => {
      let index = 0;
      const position = getPos();
      editor.state.doc.descendants((candidate, pos) => {
        if (position !== undefined && pos < position && candidate.type.name === node.type.name) index++;
      });
      return index;
    }
  });
  const kind = node.attrs.kind as string;
  const config = node.attrs.config as string;
  const issues = instructionIssues(kind, config);
  const block: InstructionBlockState | null = task ? {
    nodeId: task.nodeId,
    promptKind: task.promptKind ?? 'task',
    promptKey: task.promptKey,
    index: index_0,
    kind,
    config,
    touched,
    issues
  } : null;
  const target = block && instructionTarget(block);
  const serializedBlock = JSON.stringify(block);
  const dispatch = builder?.dispatch;
  useLayoutEffect(() => {
    if (serializedBlock !== 'null') dispatch?.({
      type: 'instructionBlock',
      id,
      block: JSON.parse(serializedBlock)
    });
  }, [dispatch, id, serializedBlock]);
  useEffect(() => () => dispatch?.({
    type: 'instructionBlock',
    id,
    block: null
  }), [dispatch, id]);
  const focusRequest = builder?.state.instructionFocusRequest;
  useEffect(() => {
    if (focusRequest?.target !== target || !target) return;
    root.current?.scrollIntoView({
      block: 'center'
    });
    root.current?.focus({
      preventScroll: true
    });
    dispatch?.({
      type: 'instructionFocused'
    });
  }, [dispatch, focusRequest, target]);
  const visible = touched || builder?.state.showInstructionErrors;
  const backendIssues = builder ? visibleValidations(builder.state).flatMap(validation => validation.details).filter(detail => detail.instruction && detail.blockTarget === target).map(detail_0 => ({
    path: detail_0.instruction?.path ?? '',
    message: detail_0.message
  })) : [];
  const errors = visible ? [...(issues ?? []), ...backendIssues] : [];
  return <fieldset ref={root} tabIndex={-1} className={cx('min-w-0 rounded-default', errors.length > 0 && 'border border-error p-2')} aria-describedby={errors.length ? `${id}-error` : undefined} onBlur={event => {
    const boundary = event.currentTarget;
    if (boundary.contains(event.relatedTarget)) return;
    // Enter briefly blurs the title before focusing the first item.
    queueMicrotask(() => {
      if (boundary.isConnected && !boundary.contains(document.activeElement)) setTouched(true);
    });
  }}>
      {children}
      {errors.length > 0 && <div id={`${id}-error`} role="alert" className="mt-2 flex flex-col gap-1 text-caption-2 text-error">
          {errors.map(error => <p key={error.path}>{error.message}</p>)}
        </div>}
    </fieldset>;
};
export default InstructionValidationBoundary;